import React, {useMemo, useState} from 'react';
import APIServer from '@taopix/taopix-core/dist/types/Service/APIServer';
import {AssetMetaInterface, DesignerSettings} from '@taopix/taopix-core/dist/types/types';
import {PictureSortType, PictureUsage} from '../AddPicturesButton';
import {useExperience} from '../../Experience/ExperienceProvider';
import ProjectView from '../Views/ProjectView';
import PreviousUploads from '../PreviousUploads';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {Checkbox, Vertical, Horizontal} from '@taopix/taopix-design-system';
import MediaGalleryOptionsPopout, {
  PictureSortDirection,
  ThumbnailViewMode,
} from '../../MediaGallery/MediaGalleryOptionsPopout';
import {useTranslation} from 'react-i18next';
import {ThumbnailLayout} from '../../MediaGallery/Thumbnails/MediaGalleryThumbnailFactory';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';

export type PreviousUploadsProps = {
  usage: PictureUsage;
  server: APIServer;
  settings: DesignerSettings;
  projectImages: AssetMetaInterface[];
};

export type PreviousUploadComponentProps = PreviousUploadsProps & {
  sortType: PictureSortType;
  hideUsed: boolean;
  thumbnailLayout: ThumbnailLayout;
};

const PreviousUploadsFactory = (props: PreviousUploadsProps) => {
  const ui = useUiState();
  const {experience} = useExperience();
  const {t} = useTranslation();
  const [loaded, setLoaded] = useState(false);

  const [sortType, setSortType] = useState<PictureSortType>(
    experience.SETTINGS.previousUploadsWindow.defaultSortOrder === 'dt'
      ? PictureSortType.DateTaken
      : experience.SETTINGS.previousUploadsWindow.defaultSortOrder === 's'
      ? PictureSortType.Size
      : experience.SETTINGS.previousUploadsWindow.defaultSortOrder === 'fn'
      ? PictureSortType.Filename
      : experience.SETTINGS.previousUploadsWindow.defaultSortOrder === 'o'
      ? PictureSortType.Orientation
      : experience.SETTINGS.previousUploadsWindow.defaultSortOrder === 'prj'
      ? PictureSortType.Project
      : PictureSortType.DateTaken
  );

  const [sortDirection, setSortDirection] = useState<PictureSortDirection>('asc');

  const onChangeSortDirection = (isReversed: boolean) => {
    setSortDirection(isReversed ? 'desc' : 'asc');
  };

  const [thumbnailLayout, setThumbnailLayout] = useState<ThumbnailLayout>(
    experience.SETTINGS.previousUploadsWindow.defaultPictureListMode === 'fft'
      ? ThumbnailViewMode.Full
      : experience.SETTINGS.previousUploadsWindow.defaultPictureListMode === 'st'
      ? ThumbnailViewMode.Square
      : experience.SETTINGS.previousUploadsWindow.defaultPictureListMode === 'fil'
      ? ThumbnailViewMode.Detailed
      : ThumbnailViewMode.Full
  );

  const [hideUsed, setHideUsed] = useState<boolean>(false);

  const onHideUsed = () => {
    setHideUsed(!hideUsed);
  };

  const onChangeThumbnailView = (newViewMode: ThumbnailLayout) => {
    setThumbnailLayout(newViewMode);
  };

  const content = useMemo(() => {
    if (PictureSortType.Project === sortType) {
      return (
        <ProjectView
          {...props}
          sortType={sortType}
          hideUsed={hideUsed}
          thumbnailLayout={thumbnailLayout}
          onPopulated={() => setLoaded(true)}
        />
      );
    }
    return (
      <PreviousUploads
        {...props}
        sortType={sortType}
        sortDirection={sortDirection}
        hideUsed={hideUsed}
        thumbnailLayout={thumbnailLayout}
        onPopulated={() => setLoaded(true)}
      />
    );
  }, [sortType, sortDirection, thumbnailLayout, hideUsed]);

  return (
    <>
      {loaded && (
        // Only show the filter options if there is some data in the view
        <div className={'flex justify-between space-x-md pb-sm'}>
          <Checkbox
            label={t('str_LabelHideUsedPictures')}
            id="hide-used-pics-dialog"
            onChange={onHideUsed}
            defaultChecked={hideUsed}
          />

          <MediaGalleryOptionsPopout
            selectedPictureAction={() => {}}
            onSort={setSortType}
            onChangeSortDirection={onChangeSortDirection}
            closeOnChange
            onChangeThumbnailView={onChangeThumbnailView}
            buttonId="media-options-menu-button-previous-uploads"
            anchorOrigin={{
              vertical: Vertical.Bottom,
              horizontal: Horizontal.Left,
            }}
            transformOrigin={{
              vertical: Vertical.Top,
              horizontal: Horizontal.Left,
            }}
            repositionOnResize={true}
            displayMode={ui.state.isSmallScreen ? 'modal' : 'positioned'}
            onHideUsed={ui.state.isSmallScreen ? onHideUsed : undefined}
            showSortByProject
            edgeBehaviour="flip"
            showRemoveImages={false}
            defaultSortType={sortType}
            defaultSortDirection={sortDirection}
            defaultThumbnailStyle={thumbnailLayout}
            theme={'tpx-theme-dialogs'}
            themeTip={'Dialogs'}
          />
        </div>
      )}
      {content}
    </>
  );
};

export default PreviousUploadsFactory;
